import React, { useEffect, useState } from 'react';
import DashboardLayout from '../dashboardLayout';
import './floor-plan.css';
import { createMasjidApi } from '../../../api/adminApi';
import { validateMasjidData } from '../../../types/masjid.types';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Button, TextField, Select } from '@mui/material';
import FloorPlanBuilder from '../../../components/common/FloorPlanBuilder';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import { createFloorValidateField } from '../../../helpers/validateField';

function CreateFloorPlan() {
	const InitialState = {
		masjidName: '',
		cityName: '',
		masjidPicture: '',
		floorName: '',
		floorCode: 'G',
		rowCount: 3,
		columnCount: 3,
	};

	const [formData, setFormData] = useState(InitialState);
	const [errors, setErrors] = useState({});
	const [floorErr, setFloorErr] = useState({});
	const [floorData, setFloorData] = useState({ floors: [] });
	const [loading, setLoading] = useState(false);
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const {
		masjidName,
		cityName,
		masjidPicture,
		floorName,
		floorCode,
		rowCount,
		columnCount,
	} = formData;

	const ErrorMessage = ({ name }) => {
		return errors[name] && <p className='error-field'>{errors[name]}</p>;
	};

	const handleChange = (event) => {
		const { name, value } = event.target;
		const fieldError = createFloorValidateField(name, value);

		setFormData((prevData) => ({
			...prevData,
			[name]:
				name === 'rowCount' || name === 'columnCount'
					? parseInt(value, 10)
					: value,
		}));

		if (fieldError) {
			setErrors({ [name]: fieldError });
		} else {
			setErrors({});
		}
	};

	function generateFloor() {
		if (formData?.floorName) {
			setFloorErr((prevData) => ({
				...prevData,
				['floorName']: '',
			}));
			const newFloor = {
				floorName,
				floorCode,
				capacity: rowCount * columnCount,
				rows: Array.from({ length: rowCount }, (_, i) => ({
					rowName: formData?.floorName?.charAt(0) + getRowName(i),
					seats: Array.from({ length: columnCount }, (_, j) => ({
						number: String(i * columnCount + j + 1),
						isReservedForSpecialGuest: false,
						notes: ''
					})),
				})),
			};

			function getRowName(index) {
				const baseChar = 65; // ASCII code for 'A'
				let rowName = '';

				while (index >= 0) {
					const charCode = baseChar + (index % 26);
					rowName = String.fromCharCode(charCode) + rowName;
					index = Math.floor(index / 26) - 1;
				}
				return rowName;
			}

			setFloorData((prevFloorData) => ({
				name: masjidName,
				city: cityName,
				masjidPicture: masjidPicture || undefined,
				floors: [...(prevFloorData?.floors || []), newFloor],
			}));

			setFormData((prevData) => ({
				...prevData,
				floorName: '',
				floorCode: 'G',
				rowCount: 3,
				columnCount: 3,
			}));
		} else {
			setFloorErr((prevData) => ({
				...prevData,
				['floorName']: 'Floor Name is required',
			}));
		}
	}

	function handleModifyRow(floorIndex, updatedRows) {
		setFloorData((prevFloorData) => {
			const updatedFloors = [...prevFloorData.floors];
			updatedFloors[floorIndex].rows = updatedRows;
			updatedFloors[floorIndex].capacity = updatedRows.reduce(
				(totalSeats, row) => totalSeats + row.seats.length,
				0
			);
			return {
				...prevFloorData,
				floors: updatedFloors,
			};
		});
	}

	function handleSubmit() {
		const formErrors = {};
		Object.keys(formData).forEach((name) => {
			const value = formData[name];
			const fieldError = createFloorValidateField(name, value);
			if (fieldError) {
				formErrors[name] = fieldError;
			}
		});

		setErrors(formErrors);

		if (floorData?.floors?.length > 0) {
			setFloorErr({});
		} else {
			setFloorErr((prevData) => ({
				...prevData,
				['floorData']: 'Floor is required',
			}));
		}
		if (
			Object.keys(formErrors).length === 0 &&
			Object.keys(floorErr).length === 0
		) {
			// Validate masjid data
			const validation = validateMasjidData(floorData);
			if (!validation.isValid) {
				console.error('Validation errors:', validation.errors);
				return;
			}

			dispatch(
				createMasjidApi(
					floorData,
					setLoading,
					(data) => {
						console.log('Masjid created:', data);
						navigate(-1);
					},
					(error) => {
						console.error('Error creating masjid:', error);
					}
				)
			);
		}
	}

	return (
		<DashboardLayout>
			<div className='floor-map' style={{ maxWidth: '1080px' }}>
				<div className='controls'>
					<div className='back-btn-wrap'>
						<Button
							variant='contained'
							className='back-btn'
							onClick={() => navigate(-1)}
						>
							<i className='fa-solid fa-arrow-left'></i> Back
						</Button>
					</div>
					<h1>Masjid Details</h1>
					<br />
					<div className='form-group-control'>
						<div className='form-group w50'>
							<TextField
								type='text'
								variant='outlined'
								label='Masjid Name:'
								name='masjidName'
								value={formData?.masjidName}
								onChange={handleChange}
							/>
							{errors?.masjidName && (
								<p className='error-msg'>
									<ErrorMessage name='masjidName' />
								</p>
							)}
						</div>
						<div className='form-group w50'>
							<TextField
								type='text'
								variant='outlined'
								label='City Name:'
								name='cityName'
								value={formData?.cityName}
								onChange={handleChange}
							/>
							{errors?.cityName && (
								<p className='error-msg'>
									<ErrorMessage name='cityName' />
								</p>
							)}
						</div>
					</div>
					<div className='form-group-control'>
						<div className='form-group w50'>
							<TextField
								type='text'
								variant='outlined'
								label='Masjid Picture URL (Optional):'
								name='masjidPicture'
								value={formData?.masjidPicture}
								onChange={handleChange}
							/>
						</div>
					</div>
					<hr
						style={{
							width: '100%',
							backgroundColor: 'black',
							fontWeight: 'bold',
						}}
					/>
					<h1>Masjid Floor Info</h1>
					<br />
					<div className='form-group-control fields'>
						<div className='form-group w100'>
							<TextField
								variant='outlined'
								type='text'
								label='Floor Name:'
								name='floorName'
								value={formData?.floorName}
								onChange={handleChange}
							/>
							{floorErr?.floorName && (
								<p className='error-msg'>{floorErr?.floorName}</p>
							)}
						</div>
						<div className='form-group w50 select'>
							<FormControl fullWidth>
								<InputLabel id='floor-code-label'>Floor Code</InputLabel>
								<Select
									labelId='floor-code-label'
									id='floor-code-select'
									value={formData?.floorCode}
									name='floorCode'
									label='Floor Code'
									onChange={handleChange}
								>
									<MenuItem value='G'>G - Ground Floor</MenuItem>
									<MenuItem value='F1'>F1 - First Floor</MenuItem>
									<MenuItem value='F2'>F2 - Second Floor</MenuItem>
									<MenuItem value='F3'>F3 - Third Floor</MenuItem>
									<MenuItem value='B1'>B1 - Basement 1</MenuItem>
									<MenuItem value='B2'>B2 - Basement 2</MenuItem>
								</Select>
							</FormControl>
						</div>
						<div className='form-group w50'>
							<TextField
								type='number'
								variant='outlined'
								label='Number of Rows:'
								name='rowCount'
								value={formData?.rowCount}
								onChange={handleChange}
							/>
							{errors?.rowCount && (
								<p className='error-msg'>
									<ErrorMessage name='rowCount' />
								</p>
							)}
						</div>
						<div className='form-group w50'>
							<TextField
								type='number'
								variant='outlined'
								label='Number of Columns:'
								name='columnCount'
								value={formData?.columnCount}
								onChange={handleChange}
							/>
							{errors?.columnCount && (
								<p className='error-msg'>
									<ErrorMessage name='columnCount' />
								</p>
							)}
						</div>
					</div>
					<Button
						variant='outlined'
						className='generate-floor'
						onClick={generateFloor}
					>
						Add Floor
					</Button>
					{floorErr?.floorData && (
						<p className='error-msg floor-msg'>{floorErr?.floorData}</p>
					)}
				</div>

				<FloorPlanBuilder
					floors={floorData?.floors || []}
					onModifyFloor={handleModifyRow}
				/>

				<div className='submit-btn-wrapper'>
					<Button variant='contained' onClick={handleSubmit}>
						Submit Changes
					</Button>
				</div>
			</div>
		</DashboardLayout>
	);
}

export default CreateFloorPlan;
